/**
 * Route Error Boundary Component
 * Handles errors at the route level for React Router
 */

import { AlertTriangle, Home, RefreshCw, ArrowLeft, Copy, Check } from 'lucide-react';
import { useState } from 'react';
import { useRouteError, isRouteErrorResponse, useNavigate } from 'react-router-dom';

import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';

export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  let errorMessage = 'An unexpected error occurred';
  let errorStatus: number | undefined;
  let errorStatusText: string | undefined;
  let errorDetails: string | undefined;

  // Handle different error types
  if (isRouteErrorResponse(error)) {
    errorStatus = error.status;
    errorStatusText = error.statusText;
    errorMessage = error.data?.message || error.statusText || errorMessage;
  } else if (error instanceof Error) {
    errorMessage = 'Something went wrong while loading this page';
    errorDetails = import.meta.env.DEV ? error.message : undefined;
  }

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleCopyError = async () => {
    const errorText = error instanceof Error
      ? `${error.name}: ${error.message}\n\nStack Trace:\n${error.stack || 'No stack trace available'}`
      : `Error: ${errorDetails || errorMessage}`;

    try {
      await navigator.clipboard.writeText(errorText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy error:', err);
    }
  };

  // 404 Not Found
  if (errorStatus === 404) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <Card className="max-w-2xl w-full shadow-lg">
          <CardHeader className="text-center space-y-4">
            <div className="mx-auto text-6xl font-bold text-gray-300">404</div>
            <CardTitle className="text-2xl font-bold text-gray-900">
              Page Not Found
            </CardTitle>
            <CardDescription className="text-base text-gray-600">
              The page you're looking for doesn't exist or has been moved.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleGoBack}
                className="flex-1 gap-2"
                variant="outline"
              >
                <ArrowLeft className="w-4 h-4" />
                Go Back
              </Button>
              <Button
                onClick={handleGoHome}
                className="flex-1 gap-2"
                variant="default"
              >
                <Home className="w-4 h-4" />
                Go to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // All other errors
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <Card className="max-w-2xl w-full shadow-lg">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          {errorStatus && (
            <div className="text-4xl font-bold text-gray-300">
              Error {errorStatus}
            </div>
          )}
          <CardTitle className="text-2xl font-bold text-gray-900">
            {errorStatusText || 'Something Went Wrong'}
          </CardTitle>
          <CardDescription className="text-base text-gray-600">
            {errorMessage}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={handleRefresh}
              className="flex-1 gap-2"
              variant="default"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh Page
            </Button>
            <Button
              onClick={handleGoHome}
              className="flex-1 gap-2"
              variant="outline"
            >
              <Home className="w-4 h-4" />
              Go to Home
            </Button>
          </div>

          {/* Development Error Details */}
          {import.meta.env.DEV && errorDetails && (
            <div className="border-t pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  Development Error Details
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyError}
                  className="gap-2 text-xs"
                >
                  {copied ? (
                    <>
                      <Check className="w-3 h-3 text-green-600" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3" />
                      Copy Error
                    </>
                  )}
                </Button>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <pre className="text-xs font-mono text-red-700 overflow-x-auto whitespace-pre-wrap">
                  {errorDetails}
                </pre>
              </div>
              {error instanceof Error && error.stack && (
                <div className="bg-orange-50 border border-orange-200 rounded-md p-4">
                  <p className="text-xs font-semibold text-orange-800 mb-2">
                    Stack Trace:
                  </p>
                  <pre className="text-xs font-mono text-orange-700 overflow-x-auto whitespace-pre-wrap">
                    {error.stack}
                  </pre>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
